import './SmallCardCollection.css';
import '@ids/style-page-control';

import { useEffect, useRef, useState } from 'react';

import type { Action, Content } from '../../global-types/Content';
import SmallCard from './SmallCard';

type SmallCardCollectionProps = {
  content: Content;
  viewId: number;
  dispatchActionEvent: (action: Action) => void;
};

function SmallCardCollection({ content, viewId, dispatchActionEvent }: SmallCardCollectionProps) {
  const [activeItem, setActiveItem] = useState<number>(0);
  const smallCardsList = useRef<HTMLUListElement | null>(null);

  const smallCardsData = (content.content ?? []).filter((item) => item.contentType === 'smallCard');

  const handleScroll = (event: React.UIEvent<HTMLElement>) => {
    const container = event.target as HTMLElement;
    const li = smallCardsList.current?.querySelector('li');

    if (li) {
      const itemWidth = li.getBoundingClientRect().width;
      const newActiveItem = Math.round(container.scrollLeft / itemWidth);
      setActiveItem(newActiveItem);
    }
  };

  useEffect(() => {
    if (smallCardsList.current) {
      (smallCardsList.current as HTMLElement).style.setProperty(
        '--small-cards-content-count',
        smallCardsData.length.toString(),
      );
    }
  }, [smallCardsData.length]);

  const handleNextContentClick = (_e: React.MouseEvent, index: number) => {
    const list = smallCardsList.current;
    const li = list?.querySelector('li');
    if (list && li) {
      const itemWidth = li.offsetWidth;
      list.scrollTo({
        left: index * itemWidth,
        behavior: 'smooth',
      });
      setActiveItem(index);
    }
  };

  const collectionTitle = content.contentViews?.[0]?.viewElements?.texts?.[0];

  return (
    smallCardsData.length > 2 && (
      <div className="small-card-collection">
        <h2 className={`if typography heading ${collectionTitle?.size}`}>{collectionTitle?.value}</h2>
        <ul ref={smallCardsList} className="small-cards" onScroll={handleScroll}>
          {smallCardsData.map((item) => (
            <SmallCard key={item.id} content={item} viewId={viewId} dispatchActionEvent={dispatchActionEvent} />
          ))}
        </ul>
        <nav className="if page-control small-card-pagination" aria-label="Pagination Navigation">
          <ul className="if page-control-list dotted medium">
            {smallCardsData.map((item, index) => (
              <li
                key={`${item.contentViews?.[0]?.viewElements?.texts?.[0]?.value}-${index}`}
                className="if page-control-item"
              >
                <button
                  type="button"
                  className={`if page-control-button ${activeItem === index ? 'current' : ''}`}
                  aria-label={`Go to page ${index + 1}`}
                  onClick={(e) => handleNextContentClick(e, index)}
                >
                  <div className="if page-control-dot" aria-hidden="true"></div>
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    )
  );
}

export default SmallCardCollection;
